"use client";
import styles from "./Navbar.module.scss";
import { MenuItemType, MenuType, PlatformType } from "../../types/baseTypes";
import {
  OptionsHeaderType,
  OptionsPromoBoxConfigType,
} from "@/types/OptionType";
import { NavigationOptionsType } from "@/types/ModuleOptionsType";
import { Logos } from "./Logos/Logos";
import { Menu } from "./Menu/Menu";
import { NavbarPill } from "./NavbarPill";
import { Socials } from "./Socials/Socials";
import { LanguageToggle } from "./Language/LanguageToggle";
import { Hamburger } from "./Hamburger/Hamburger";
import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
// import useIsomorphicLayoutEffect from "@/hooks/useIsomorphicLayoutEffect";
import { useAppContext } from "../../context/AppContext";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Post } from "@/wp/types/client";
import { CustomLink } from "../CustomLink";
import useScrollLock from "@/hooks/useScrollLock";

interface NavbarProps {
  menu: MenuType;
  searchPageSlug: string;
  socialMedia: PlatformType;
  navbarOption: NavbarOptionType;
  pageData?: Post;
  isHome: boolean;
  isPage: boolean;
  isTextHeader: boolean;
  isNobleElements: boolean;
}

export type NavbarOptionType = {
  themeOption: OptionsHeaderType;
  moduleOption: NavigationOptionsType;
  promoBox: OptionsPromoBoxConfigType;
};

export const Navbar = ({
  navbarOption,
  menu,
  socialMedia,
  pageData,
  isHome,
  isPage,
  isTextHeader,
  isNobleElements,
}: NavbarProps) => {
  const { logos } = navbarOption.themeOption;
  const {
    show_box_shadow,
    show_social_icons_in_menu,
    show_search,
    show_language,
  } = navbarOption.moduleOption.header_menu;
  const [hasBackground, setHasBackground] = useState<boolean>(false);
  const { openMenu, setNavbarSize, isHomeIntroComplete } = useAppContext();
  const { getLabel } = useGlobalContext();

  const correctNobleLogo = () => {
    if (isNobleElements) {
      return logos.filter((logo) => logo.type === "noble-elements");
    }
    return logos.filter((logo) => logo.type === "noble-bc");
  };

  useEffect(() => {
    const navbar = document.getElementById("navbar-main");
    if (!navbar) return;

    const updateSize = () => {
      if (navbar) {
        const { offsetWidth, offsetHeight } = navbar;
        setNavbarSize({ width: offsetWidth, height: offsetHeight });
      }
    };

    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(navbar);
    updateSize();

    return () => resizeObserver.disconnect();
  }, [setNavbarSize]);

  useLenis(({ scroll }) => {
    setHasBackground(scroll > 20);
  });

  const { toggleLock } = useScrollLock(false);

  useEffect(() => {
    toggleLock(openMenu);
  }, [openMenu, toggleLock]);

  const promoLink: MenuItemType = {
    menu_order: 0,
    url: getLabel("vertriebsportal_link", "https://noble-bc.de/"),
    title: getLabel("navbar_promo_button", "Vertriebsportal"),
    target: "_blank",
    classes: ["is-hidden-after-navbar-breakpoint"],
    post_name: "promo-link",
    aria_label: getLabel("vertriebsportal_aria", "Vertriebsportal"),
  };

  const NAVJSX = (
    <>
      <Menu
        menu={menu}
        pageData={pageData}
        isHome={isHome}
        promoBoxOption={navbarOption.promoBox}
        socialMedia={socialMedia}
        promoLink={promoLink}
        isNobleElements={isNobleElements}
      />

      {show_social_icons_in_menu && <Socials platforms={socialMedia} />}
      {show_language && pageData && (
        <LanguageToggle
          addWrapper={true}
          post={pageData}
          extraClass="is-hidden-before-navbar-breakpoint"
        />
      )}

      <CustomLink
        href={promoLink.url}
        target={promoLink.target}
        aria-label={promoLink.aria_label}
        className={`${styles["navbar-promo-button"]} navbar-link`}
        rel="noreferrer"
      >
        {promoLink.title}
      </CustomLink>
    </>
  );

  return (
    <header>
      <nav
        id="navbar"
        data-has-bg={isHome || (isPage && !isTextHeader) ? hasBackground : true}
        data-show-shadow={show_box_shadow}
        data-home-intro-hidden={isHome && !isHomeIntroComplete && !openMenu}
        //! please dont remove scrollbar-width class
        className={`${styles["navbar"]} ${openMenu ? styles["opened"] : ""} navbar scrollbar-width`}
      >
        <div
          className={`${styles["navbar-logo"]}`}
          style={{
            opacity: isHome ? (hasBackground || openMenu ? 1 : 0) : 1,
            zIndex: 11,
          }}
        >
          <Logos logos={correctNobleLogo()} />
        </div>

        <Hamburger
          hasBackground={
            isHome || (isPage && !isTextHeader) ? hasBackground : true
          }
        />
        <FocusTrap disabled={!openMenu}>
          <div
            id="navbar-main"
            data-show-search={show_search}
            data-lenis-prevent
            className={`${styles["navbar-main"]} ${openMenu ? styles["opened"] : ""}`}
          >
            <div className={styles["navbar-main-mobile-scroll"]}>
              {isHome || isPage ? (
                <NavbarPill
                  hasBg={
                    isHome || (isPage && !isTextHeader) ? hasBackground : true
                  }
                >
                  {NAVJSX}
                </NavbarPill>
              ) : (
                NAVJSX
              )}
            </div>
          </div>
        </FocusTrap>
      </nav>
    </header>
  );
};
